/**
 * Optional live-streaming client for `@authe/streaming`.
 *
 * Transport precedence:
 *  1. `VITE_APPSYNC_ENDPOINT` set → the AppSync WebSocket transport (preferred):
 *     a shared leader socket, the active JWT as the lambda authToken, the
 *     `ping` query as an active keepalive, and the standard `subscribe`
 *     document. Bodies stay lean (header + S3 pointer) and hydrate from the CDN.
 *  2. else `VITE_STREAMING_URL` set → an SSE transport with a poll fallback.
 *  3. else `null` → no server client; every surface offers Auto-refresh
 *     (interval) and Off only, with the "Live" option removed.
 *
 * Whether a client is created at all is decided ONCE, in
 * `<DashboardStreamingProvider>`, from this build's endpoint AND the caller's
 * plan (`realtime_streaming`) — see `./serverStreaming`.
 *
 * A fresh bearer token is pulled per (re)connect from the session manager.
 */
import {
  createStreamingClient,
  createAppSyncTransport,
  createAppSyncClientPool,
  aliasEndpointResolver,
  createFallbackTransport,
  createSseTransport,
  createPollTransport,
  selectAutheStreamPayload,
  type StreamingClient,
} from "@authe/streaming";
import { AWS_REGION_ALIASES } from "@authe/fe-commons/region";
import { isDebugEnabled } from "@authe/fe-commons/logging";
import { appConfig } from "@/config";

/**
 * Normalise the configured region (an alias like `pdx`, or already a full
 * `us-west-2`) to the FULL AWS region the streaming layer keys on
 * (`failoverRegion`, `convertRegionToAlias`, the persisted last-good region).
 */
function fullRegion(regionOrAlias: string): string {
  return AWS_REGION_ALIASES[regionOrAlias] ?? regionOrAlias;
}

/** Publish a payload to an AppSync channel the caller is authorized to write to. */
export type CollabPublish = (name: string, data: unknown) => Promise<void>;

export function createDashboardStreamingClient(
  getToken: () => Promise<string | null>,
  sharingKey: string,
): StreamingClient | null {
  const token = async () => (await getToken()) ?? undefined;

  // 1. AppSync WebSocket (preferred). Region-aware: the client pool opens a
  //    socket per region endpoint, and `aliasEndpointResolver` derives the
  //    backup region's URL from the primary by swapping the alias label in the
  //    host. Failover is MANUAL (`autoRegionFailover: false`) — the connection
  //    stays on the primary and only moves on an explicit `setRegion(...)`; the
  //    active region/host is logged on every connect/open in debug mode.
  if (appConfig.appsyncEndpoint) {
    const region = fullRegion(appConfig.region);
    const pool = createAppSyncClientPool();
    const client = createStreamingClient({
      transport: createAppSyncTransport({
        resolve: pool.resolve,
        selectPayload: selectAutheStreamPayload,
      }),
      endpoint: aliasEndpointResolver(appConfig.appsyncEndpoint, region),
      region,
      autoRegionFailover: false,
      getToken: token,
      sharingKey,
    });
    exposeStreamingDebugHandle(client, region);
    return client;
  }

  // 2. SSE + poll fallback.
  const url = appConfig.streamingUrl;
  if (url) {
    const client = createStreamingClient({
      transport: createFallbackTransport([createSseTransport(), createPollTransport()]),
      endpoint: () => url,
      // The FULL region, like the AppSync path above. The streaming layer looks
      // regions up in the canonical table (`failoverRegion`, the persisted
      // last-good region), which is keyed by `us-west-2`, never by the `pdx`
      // alias — so an alias here silently disabled failover and made the
      // persisted region unreadable, on the two transports whose whole reason
      // for existing is a degraded network.
      region: fullRegion(appConfig.region),
      getToken: token,
      sharingKey,
    });
    // The console handle belongs to BOTH transports. It was attached only on the
    // AppSync path, so on the SSE/poll fallback — the deployments most likely to
    // need it — `__autheStreaming` was simply absent, and an operator who knew
    // to reach for it got `undefined` with nothing to say why.
    exposeStreamingDebugHandle(client, fullRegion(appConfig.region));
    return client;
  }

  // 3. No live transport configured.
  return null;
}

/**
 * A publish runner for user-authenticated AppSync mutations (collaboration),
 * or `null` when the AppSync transport isn't configured (publish only works over
 * AppSync; the SSE/poll fallback is receive-only). Publish is a plain HTTP
 * GraphQL POST (no socket), pulling a fresh bearer per call so a rotated token
 * keeps publishing.
 */
export function createDashboardCollabPublish(
  getToken: () => Promise<string | null>,
  client: Pick<StreamingClient, "getRegion">,
): CollabPublish | null {
  if (!appConfig.appsyncEndpoint) return null;
  const pool = createAppSyncClientPool();
  const endpoint = aliasEndpointResolver(appConfig.appsyncEndpoint, fullRegion(appConfig.region));
  return async (name, data) => {
    const token = (await getToken()) ?? undefined;
    if (!token) throw new Error("Collaboration requires an authenticated session");
    await pool.publishFor(endpoint(client.getRegion()))(name, data, token);
  };
}

/**
 * In debug mode (`?debug=1`), expose the streaming client on
 * `window.__autheStreaming` so an operator can inspect/switch the active region
 * by hand — region failover is manual (`autoRegionFailover: false`), so this is
 * how you exercise the backup without waiting for an auto-hop:
 *
 *   __autheStreaming.getRegion()              // → "us-west-2"
 *   __autheStreaming.setRegion("us-east-1")   // reconnect onto the backup
 *   __autheStreaming.stats()                  // region/leader/followers/channels/handlers
 *
 * No-op (and never attached) outside debug mode, so prod bundles stay clean.
 */
function exposeStreamingDebugHandle(client: StreamingClient, primaryRegion: string): void {
  if (typeof window === "undefined" || !isDebugEnabled()) return;
  (window as unknown as { __autheStreaming?: unknown }).__autheStreaming = {
    client,
    primaryRegion,
    getRegion: () => client.getRegion(),
    setRegion: (region: string) => client.setRegion(region),
    /** Structured snapshot: region, leadership, followers, channels, handlers, dedupe window. */
    stats: () => client.snapshot(),
    /** How many OTHER tabs share this client (followers when this tab leads). */
    followers: () => client.followerCount(),
  };
}

/**
 * Whether this BUILD wires a live push-streaming endpoint.
 *
 * Half of a decision, and not the half a surface may gate on: read
 * `useServerStreaming()` (`./serverStreaming`), which also asks the plan. A
 * guard test fails any other module that reads this.
 */
export const isStreamingConfigured = Boolean(
  appConfig.appsyncEndpoint || appConfig.streamingUrl,
);
